// Record endpoints on typed contracts (`@dataroom/contracts/records`, `/query`): list, query, CRUD,
// restore, history and history restore, links, refs, the bulk mutation, undo tokens and the
// progress of background record operations. Owner record-ops in wave 1, record-runtime in wave 2.
import {
  addLinkContract,
  bulkContract,
  createRecordContract,
  deleteRecordContract,
  getRecordContract,
  listLinksContract,
  listRecordsContract,
  recordChangesContract,
  recordJobContract,
  recordRefsContract,
  removeLinkContract,
  restoreRecordContract,
  restoreRecordToContract,
  restoreValueContract,
  undoContract,
  updateLinkContract,
  updateRecordContract,
} from '@dataroom/contracts/records';
import { queryRecordsContract } from '@dataroom/contracts/query';
import type {
  BulkMutationInput,
  BulkMutationResult,
  CreateLinkInput,
  CreateRecordInput,
  FilterNode,
  QueryRequest,
  RecordJobDTO,
  RestoreRecordInput,
  RestoreValueInput,
  SortSpec,
  UpdateLinkInput,
  UpdateRecordInput,
} from '@dataroom/core';
import type { Http } from './http';
import { type CallOptions, createCall } from './typed';

export type ListRecordsParams = {
  limit?: number;
  cursor?: string | null;
  q?: string;
  count?: 'none' | 'estimate' | 'exact';
  sort?: SortSpec[];
  fields?: string[];
  filter?: FilterNode | null;
};

/** `expectedVersion` for writes without a body (delete, restore, unlink): a stale one answers 409. */
export type VersionOptions = { expectedVersion?: number };

const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        reject(signal.reason instanceof Error ? signal.reason : new Error('aborted'));
      },
      { once: true },
    );
  });

export const recordsApi = (http: Http) => {
  const call = createCall(http);
  const job = (id: string, options?: CallOptions) => call(recordJobContract, { params: { id } }, options);
  return {
    list: (objectKey: string, params: ListRecordsParams = {}, options?: CallOptions) =>
      call(
        listRecordsContract,
        {
          params: { key: objectKey },
          query: {
            limit: params.limit,
            cursor: params.cursor ?? undefined,
            q: params.q,
            count: params.count,
            sort: params.sort?.map((s) => `${s.field}:${s.dir}`),
            fields: params.fields,
            filter: params.filter ?? undefined,
          },
        },
        options,
      ),
    query: (objectKey: string, request: QueryRequest, options?: CallOptions) =>
      call(queryRecordsContract, { params: { key: objectKey }, body: request }, options),
    get: (objectKey: string, id: string, opts: { includeSources?: boolean } = {}) =>
      call(getRecordContract, {
        params: { key: objectKey, id },
        query: { include: opts.includeSources ? 'sources' : undefined },
      }),
    /** Create; the result carries `undo` (undo deletes the record). */
    create: (objectKey: string, input: CreateRecordInput) =>
      call(createRecordContract, { params: { key: objectKey }, body: input }),
    /** Update (stage changes too); `expectedVersion` gives field-level 409 conflicts; returns `undo`. */
    update: (objectKey: string, id: string, input: UpdateRecordInput) =>
      call(updateRecordContract, { params: { key: objectKey, id }, body: input }),
    /** Soft delete (cascading): the deleted ids and `undo`. */
    delete: (objectKey: string, id: string, opts: VersionOptions = {}) =>
      call(deleteRecordContract, { params: { key: objectKey, id }, query: opts }),
    restore: (objectKey: string, id: string, opts: VersionOptions = {}) =>
      call(restoreRecordContract, { params: { key: objectKey, id }, query: opts }),
    /** Change history, newest first, with the operation and origin of each change. */
    changes: (objectKey: string, id: string, opts: { limit?: number; before?: string } = {}) =>
      call(recordChangesContract, { params: { key: objectKey, id }, query: opts }),
    /** "Restore this value" of a history entry, as a new undoable change. */
    restoreValue: (objectKey: string, id: string, changeId: string, input: RestoreValueInput) =>
      call(restoreValueContract, { params: { key: objectKey, id, changeId }, body: input }),
    /** "Restore record to this point" (an instant or right after a change), as one undoable change. */
    restoreTo: (objectKey: string, id: string, input: RestoreRecordInput) =>
      call(restoreRecordToContract, { params: { key: objectKey, id }, body: input }),
    refs: (ids: string[]) => call(recordRefsContract, { query: { ids } }),
    links: {
      list: (objectKey: string, id: string, relationField: string, opts: { limit?: number } = {}) =>
        call(listLinksContract, { params: { key: objectKey, id, relation: relationField }, query: opts }),
      add: (objectKey: string, id: string, relationField: string, input: CreateLinkInput) =>
        call(addLinkContract, { params: { key: objectKey, id, relation: relationField }, body: input }),
      update: (
        objectKey: string,
        id: string,
        relationField: string,
        linkId: string,
        input: UpdateLinkInput,
      ) =>
        call(updateLinkContract, {
          params: { key: objectKey, id, relation: relationField, linkId },
          body: input,
        }),
      remove: (
        objectKey: string,
        id: string,
        relationField: string,
        linkId: string,
        opts: VersionOptions = {},
      ) =>
        call(removeLinkContract, {
          params: { key: objectKey, id, relation: relationField, linkId },
          query: opts,
        }),
    },
    /**
     * The one bulk mutation (plan §7.3): per-record results, `dryRun`, one `undo` for the whole
     * operation, and a `jobId` above 1,000 records (follow it with `job` or `waitForJob`).
     */
    bulk: async (objectKey: string, input: BulkMutationInput, options?: CallOptions) =>
      (await call(bulkContract, { params: { key: objectKey }, body: input }, options)) as BulkMutationResult,
    /** Revert an operation with its undo token: per-record results and a `redo` token. */
    undo: (token: string, options?: CallOptions) => call(undoContract, { params: { token } }, options),
    /** Progress of a background record operation. */
    job,
    /**
     * Poll a background record operation until it finishes; `onProgress` sees every snapshot. Use
     * the realtime `job:<id>` topic instead once collab-kit provides it.
     */
    waitForJob: async (
      id: string,
      opts: { intervalMs?: number; signal?: AbortSignal; onProgress?: (job: RecordJobDTO) => void } = {},
    ): Promise<RecordJobDTO> => {
      for (;;) {
        const current = await job(id, opts.signal ? { signal: opts.signal } : undefined);
        opts.onProgress?.(current);
        if (current.status === 'succeeded' || current.status === 'failed') return current;
        await sleep(opts.intervalMs ?? 500, opts.signal);
      }
    },
  };
};
